import { useCallback, useEffect, useRef, useState } from 'react';

import { de } from '../../i18n/de';
import { speechService } from '../../core/speech/SpeechService';
import { useProfileStore } from '../../core/profile/ProfileStore';
import {
  TOTAL_TRIALS,
  TRIAL_TIMEOUT_MS,
  planTrials,
  type PlannedTrial,
} from '../../core/calibration/trials';
import type { CalibrationTrial } from '../../core/types';

/**
 * The Zielübung — a functional access assessment (§5.6).
 *
 * A single high-contrast target appears at one of nine positions, shrinking across the run. What it
 * measures is what the design cannot otherwise know: whether this person's difficulty is size,
 * side, aim or something that rules out direct touch altogether.
 *
 * **Full-screen, and that is forced rather than chosen.** A 34 mm target needs 177 px; the app
 * frame's middle band gives 3 × 3 cells of 136 px (26.2 mm), so the largest target simply does not
 * fit inside the frame. Full screen gives 48 mm cells. It also keeps the quick fires out of reach of
 * a stray aim, which matters when the exercise is deliberately encouraging fast, imprecise taps.
 *
 * `Abbrechen` also calls `speechService.stop()`, so non-negotiable §3.3 (STOP reachable everywhere)
 * is honoured here rather than excepted.
 *
 * **Stoppable at any point.** Fatigue is the likeliest reason a run ends early — capability at 18:00
 * is far below capability at 10:00 — so quitting halfway yields a proposal from partial data marked
 * provisional, not a lost session.
 */

interface ZieluebungProps {
  onFinish: (trials: CalibrationTrial[]) => void;
  onCancel: () => void;
}

export function Zieluebung({ onFinish, onCancel }: ZieluebungProps): React.JSX.Element {
  const pxPerMm = useProfileStore((s) => s.settings.pxPerMm);
  const [plan] = useState<PlannedTrial[]>(() => planTrials(Math.random));
  const [index, setIndex] = useState(0);
  const [trials, setTrials] = useState<CalibrationTrial[]>([]);

  const shownAtRef = useRef(performance.now());
  const targetRef = useRef<HTMLButtonElement>(null);
  const current = plan[index];

  const record = useCallback(
    (trial: CalibrationTrial) => {
      setTrials((previous) => {
        const next = [...previous, trial];
        if (next.length >= plan.length) onFinish(next);
        return next;
      });
      setIndex((previous) => previous + 1);
    },
    [plan.length, onFinish],
  );

  // Never touched: the target waited and nothing arrived anywhere on screen. This is the signal that
  // separates a field loss from a miss — a target the person did not perceive at all.
  useEffect(() => {
    if (!current) return;
    shownAtRef.current = performance.now();

    const timer = setTimeout(() => {
      record({
        position: current.position,
        targetMm: current.targetMm,
        hit: false,
        offset: null,
        timeToFirstTouchMs: null,
        neverTouched: true,
      });
    }, TRIAL_TIMEOUT_MS);

    return () => clearTimeout(timer);
  }, [current, record]);

  const onScreenTouch = (event: React.PointerEvent<HTMLDivElement>): void => {
    if (!current) return;

    const rect = targetRef.current?.getBoundingClientRect();
    if (!rect) return;

    const point = { x: event.clientX, y: event.clientY };
    const centre = { x: (rect.left + rect.right) / 2, y: (rect.top + rect.bottom) / 2 };
    const hit =
      point.x >= rect.left && point.x <= rect.right && point.y >= rect.top && point.y <= rect.bottom;

    record({
      position: current.position,
      targetMm: current.targetMm,
      hit,
      // Signed, from the target centre: a consistent direction across many trials is the motor-bias
      // finding, and only the offsets carry it.
      offset: { x: Math.round(point.x - centre.x), y: Math.round(point.y - centre.y) },
      timeToFirstTouchMs: Math.round(performance.now() - shownAtRef.current),
      neverTouched: false,
    });
  };

  const sizePx = (current?.targetMm ?? 0) * pxPerMm;
  const row = current ? Math.floor(current.position / 3) : 0;
  const column = current ? current.position % 3 : 0;

  return (
    <div
      data-testid="zieluebung"
      onPointerDown={onScreenTouch}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--bg)',
        color: 'var(--fg)',
        padding: 'calc(var(--safe-top) + 4px) 8px calc(var(--safe-bottom) + 4px)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          flex: '0 0 auto',
        }}
      >
        <span data-testid="zieluebung-progress" style={{ fontSize: 'var(--font-body)' }}>
          {trials.length} / {TOTAL_TRIALS}
        </span>
        <button
          type="button"
          data-testid="zieluebung-cancel"
          onClick={(event) => {
            event.stopPropagation();
            // STOP is reachable from every surface, including this one (§3.3).
            speechService.stop();
            onFinish(trials);
          }}
          style={{
            minHeight: 'var(--min-target)',
            paddingInline: 16,
            fontSize: 'var(--font-button)',
            background: 'var(--key-bg)',
            color: 'var(--key-fg)',
            border: '3px solid var(--key-border)',
            borderRadius: 'var(--radius)',
          }}
        >
          {de.stopExercise}
        </button>
      </div>

      {/* The 3 × 3 field. The target is placed by grid cell, and the whole screen is the touch
          surface — a tap anywhere is recorded, because where the person aims when they miss is the
          measurement. */}
      <div
        style={{
          flex: '1 1 auto',
          minHeight: 0,
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gridTemplateRows: 'repeat(3, 1fr)',
        }}
      >
        {current ? (
          <div
            style={{
              gridRow: row + 1,
              gridColumn: column + 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <button
              ref={targetRef}
              type="button"
              data-testid="zieluebung-target"
              aria-label={de.targetHere}
              style={{
                width: sizePx,
                height: sizePx,
                minWidth: 0,
                minHeight: 0,
                borderRadius: '50%',
                // Highest-contrast element on screen, by §5.2's rule for anything the person must
                // find. Nothing else here competes for attention.
                background: 'var(--fg)',
                border: '4px solid var(--bg)',
                boxShadow: '0 0 0 4px var(--fg)',
              }}
            />
          </div>
        ) : null}
      </div>

      <p style={{ flex: '0 0 auto', fontSize: 'var(--font-body)', textAlign: 'center' }}>
        {de.targetInstruction}
      </p>
      <button
        type="button"
        data-testid="zieluebung-abort"
        onClick={(event) => {
          event.stopPropagation();
          speechService.stop();
          onCancel();
        }}
        style={{
          position: 'absolute',
          left: 8,
          bottom: 8,
          minHeight: 'var(--min-target)',
          paddingInline: 16,
          fontSize: 'var(--font-button)',
          background: 'var(--key-bg)',
          color: 'var(--key-fg)',
          border: '3px solid var(--key-border)',
          borderRadius: 'var(--radius)',
        }}
      >
        {de.cancel}
      </button>
    </div>
  );
}
